import type { CalendarMeetingItem, MeetingKind } from '@wundo/shared/calendar.ts';
import { apiFetchJson } from '@wundo/client/api.ts';

/** How many conversations are read at a time, going back through the archive. */
export const CONVERSATIONS_PAGE_SIZE = 30;

export interface ConversationsPage {
  /** Newest first, planned before past — the order the page reads in. */
  conversations: CalendarMeetingItem[];
  /** The page after this one, or null once the archive has run out. */
  next: number | null;
}

/**
 * The conversations Wundo accompanies: the meetings that sit in a thread.
 *
 * Searching and filtering by kind are the server's job, unlike the contacts page's:
 * this list is a history that keeps growing, so what is in the browser is only
 * ever the pages somebody has scrolled to and narrowing that would hide the
 * older matches.
 *
 * Addressed by user id like the rest of the calendar — the same route serves a
 * superadmin looking at someone's page, and the policy decides who may.
 */
export async function fetchConversations(
  userId: string,
  options: { search?: string; kind?: MeetingKind; page: number },
): Promise<ConversationsPage> {
  const query = new URLSearchParams({
    page: String(options.page),
    limit: String(CONVERSATIONS_PAGE_SIZE),
  });
  if (options.search) {
    query.set('search', options.search);
  }
  if (options.kind) {
    query.set('kind', options.kind);
  }

  const response = await apiFetchJson<{
    data: CalendarMeetingItem[];
    meta: { totalPages: number };
  }>(`/users/${userId}/conversations?${query.toString()}`);

  return {
    conversations: response.data,
    next: options.page < response.meta.totalPages ? options.page + 1 : null,
  };
}
